/**
 * Плейсхолдеры меты каталога.
 *
 * Значения для них должны быть всегда: и минимальная цена, и город есть на
 * любой странице каталога. Если значения не оказалось — это баг источника
 * (цена не посчиталась, запись панели ссылается на переменную, которой на
 * этом уровне фильтра нет), и чинить надо источник.
 *
 * Здесь только две вещи: список написаний плейсхолдера цены и страховка,
 * которая не даёт решётке доехать до пользователя и сообщает о ней в
 * GlitchTip, чтобы баг было видно.
 */

/**
 * Написания плейсхолдера минимальной цены, встречающиеся в записях панели.
 * Канон — `#MIN_PRICE#`, но в части записей набрано через дефис, и до этой
 * правки такие строки уходили в HTML как есть (ТЗ on-page, T-01).
 */
export const MIN_PRICE_ALIASES = ["#MIN_PRICE#", "#MIN-PRICE#", "#PRICE#"];

/**
 * Свойство, по значениям которого подставляются цены. В текстах каталога есть
 * таблица со всеми типами крюка, а у страницы своя цена только одна — её
 * подставляет `#MIN_PRICE#`. Остальные берутся по значению фильтра.
 */
export const PROP_PRICE_PROP = "tip_kryuka";

/**
 * Плейсхолдер цены по значению фильтра: `#MIN_PRICE_NESEMNYY#`.
 * Подчёркивание после `MIN_PRICE` обязательно, поэтому обычный `#MIN_PRICE#`
 * под шаблон не попадает.
 */
const PROP_PRICE_REGEX = /#MIN_PRICE_([A-Z0-9_]+)#/g;

/**
 * Строки таблиц по моделям и кузовам марки (тексты 10.2026): цена по модели —
 * `#MIN_PRICE_MODEL_RAV4#`, по кузову — `#MIN_PRICE_KUZOV_J200_2007_2021#`.
 * Без префикса — тип крюка, как и раньше.
 */
const PROP_PRICE_PREFIXES: Record<string, string> = {
  MODEL_: "model",
  KUZOV_: "kuzov",
};

/** Токены цен по значениям фильтра: `#MIN_PRICE_NESEMNYY#` → tip_kryuka `nesemnyy` */
export function findPropPriceTokens(
  text: unknown,
): { token: string; prop: string; value: string }[] {
  const found = new Map<string, { prop: string; value: string }>();
  const matches = String(text ?? "").matchAll(PROP_PRICE_REGEX);
  for (const match of matches) {
    const prefix = Object.keys(PROP_PRICE_PREFIXES).find((item) =>
      match[1].startsWith(item),
    );
    found.set(match[0], {
      prop: prefix ? PROP_PRICE_PREFIXES[prefix] : PROP_PRICE_PROP,
      value: match[1].slice(prefix?.length ?? 0).toLowerCase(),
    });
  }
  return [...found].map(([token, item]) => ({ token, ...item }));
}

/** Число товаров в наличии на странице: «Сейчас в наличии 9 фаркопов» */
export const IN_STOCK_TOKEN = "#IN-STOCK#";
/** Минимальное время установки фаркопа без электрики среди товаров страницы */
export const INSTALL_TIME_TOKEN = "#INSTALL-TIME#";
/** То же для фаркопа с подключением электрики */
export const INSTALL_TIME_EL_TOKEN = "#INSTALL-TIME-EL#";

/**
 * Время установки после «от»: «40 минут», «1 часа», «1,5 часа», «2 часов».
 * Нормативы в карточках кратны получасу, поэтому дробь — одна цифра.
 */
export function formatInstallTime(minutes: number): string {
  if (!(minutes > 0)) return "";
  if (minutes < 60) return `${minutes} минут`;
  const hours = Math.round((minutes / 60) * 10) / 10;
  const text = String(hours).replace(".", ",");
  return Number.isInteger(hours) && hours > 1
    ? `${text} часов`
    : `${text} часа`;
}

/**
 * Данные, которых может не оказаться: товаров в наличии нет, норматива
 * установки нет, по строке таблицы ничего нет в наличии. По ТЗ текстов
 * 10.2026 такое предложение или строка таблицы просто не выводится — в тексте
 * они написаны отдельно, чтобы их можно было убрать, не трогая соседние.
 */
const OPTIONAL_TOKEN_REGEX =
  /#(?:IN-STOCK|INSTALL-TIME|INSTALL-TIME-EL|MIN_PRICE_[A-Z0-9_]+)#/;

/**
 * Убирает строки таблиц и предложения с незаполненными необязательными
 * плейсхолдерами. Вызывается после подстановки: что осталось — данных нет.
 */
export function dropOptionalPlaceholders(text: unknown): string {
  const value = String(text ?? "");
  if (!OPTIONAL_TOKEN_REGEX.test(value)) return value;
  const token = OPTIONAL_TOKEN_REGEX.source;
  return (
    value
      .replace(new RegExp(`<tr>(?:(?!</tr>).)*?${token}.*?</tr>`, "gs"), "")
      // предложение: от конца предыдущего (или начала абзаца) до своей точки
      .replace(new RegExp(`\\s*[^.!?<>]*${token}[^.!?<>]*[.!?]`, "g"), "")
      .replace(/<p>\s*<\/p>/g, "")
      .replace(/<p>\s+/g, "<p>")
  );
}

/**
 * Плейсхолдеры, которые снимать нельзя: город на главном домене выбирает
 * пользователь, и `#CITY#` доезжает до браузера намеренно — подстановку
 * делает layout (components/layouts/main).
 */
const KEEP_PLACEHOLDERS = new Set(["#CITY#", "#CITY_NAME#"]);

/**
 * Плейсхолдер панели вместе с пробелами по краям: `#MARK_RUS#`, `#MIN-PRICE#`.
 * Только верхний регистр и обязательная закрывающая решётка — якоря (`#tab-1`)
 * и hex-цвета (`#FFFFFF` без закрывающей) под шаблон не подходят.
 */
const PLACEHOLDER_REGEX = /[ \t]*#[A-Z][A-Z0-9_-]*#[ \t]*/g;

/** Плейсхолдеры без значения: и незаполненные, и с опечаткой (`#MIN_RPICE#`) */
export function findUnresolvedPlaceholders(text: unknown): string[] {
  const found = String(text ?? "").match(PLACEHOLDER_REGEX) || [];
  const tokens = found
    .map((match) => match.trim())
    .filter((token) => !KEEP_PLACEHOLDERS.has(token));
  return [...new Set(tokens)];
}

/** Снимает плейсхолдеры без значения, не трогая те, что подставляются позже */
export function stripUnresolvedPlaceholders(text: unknown): string {
  const value = String(text ?? "");
  if (!value) return "";
  return value
    .replace(PLACEHOLDER_REGEX, (match) =>
      KEEP_PLACEHOLDERS.has(match.trim()) ? match : " ",
    )
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}
